/**
 * Data: what each aircraft physically is, what the fleet has done, and what it
 * can be expected to do.
 *
 * Three tabs, in the order a question usually runs. The register says which
 * airframe is which, by its board rather than by the port it happened to use.
 * Analytics is the flight log, joined to those airframes. Performance turns
 * the same flights into planning estimates, labelled as such.
 */

import { useEffect, useState, type ReactNode } from 'react';

import {
  getAnalytics,
  getPerformance,
  type AnalyticsSummary,
  type PerformanceReport,
} from '@/api/analytics';

import { AnalyticsTabView, ANALYTICS_TABS, LinkLostAlert, type AnalyticsTab } from './AnalyticsPanels';
import { DANGER, MUTED, PillButton } from './dataUi';
import { useIdentifyStore } from './identifyStore';
import { PerformancePanel } from './PerformancePanel';
import { RegisterPanel } from './RegisterPanel';

type Tab = 'register' | 'analytics' | 'performance';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'register', label: 'REGISTER' },
  { id: 'analytics', label: 'ANALYTICS' },
  { id: 'performance', label: 'PERFORMANCE' },
];

const WINDOWS = [7, 30, 90, 365];

/** Analytics move in days, not frames. */
const REFRESH_MS = 60_000;

function useLoaded<T>(load: () => Promise<{ ok: boolean; data: T | null; error: { message: string } | null }>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const run = () =>
      void load().then((result) => {
        if (cancelled) return;
        if (result.ok) {
          setData(result.data);
          setError(null);
        } else {
          setError(result.error?.message ?? 'Could not load.');
        }
      });
    run();
    const timer = window.setInterval(run, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // The caller names what the load depends on; the closure itself changes
    // every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, error };
}

function Analytics({ days, includeSimulated }: { days: number; includeSimulated: boolean }) {
  const [tab, setTab] = useState<AnalyticsTab>('outcomes');
  const version = useIdentifyStore((state) => state.version);
  const { data, error } = useLoaded<AnalyticsSummary>(
    () => getAnalytics(days, includeSimulated),
    [days, includeSimulated, version],
  );
  return (
    <>
      <Problem text={error} />
      {data ? (
        <>
          <LinkLostAlert data={data} />
          <div className="flex flex-wrap gap-1.5">
            {ANALYTICS_TABS.map((t) => (
              <PillButton key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                {t.label}
              </PillButton>
            ))}
          </div>
          <AnalyticsTabView tab={tab} data={data} />
        </>
      ) : null}
    </>
  );
}

function Performance({ includeSimulated }: { includeSimulated: boolean }) {
  const version = useIdentifyStore((state) => state.version);
  const { data, error } = useLoaded<PerformanceReport>(
    () => getPerformance(includeSimulated),
    [includeSimulated, version],
  );
  return (
    <>
      <Problem text={error} />
      {data ? <PerformancePanel report={data} includeSimulated={includeSimulated} /> : null}
    </>
  );
}

function Problem({ text }: { text: string | null }): ReactNode {
  if (!text) return null;
  return (
    <p
      className="rounded-[12px] border px-3 py-2 text-[11.5px]"
      role="status"
      style={{
        color: DANGER,
        borderColor: 'color-mix(in oklch, oklch(0.62 0.20 28) 45%, transparent)',
        background: 'color-mix(in oklch, oklch(0.62 0.20 28) 10%, transparent)',
      }}
    >
      {text}
    </p>
  );
}

export function DataView(): ReactNode {
  const [tab, setTab] = useState<Tab>('register');
  const [days, setDays] = useState(30);
  const [includeSimulated, setIncludeSimulated] = useState(false);

  return (
    <div
      className="screen-canvas absolute inset-0"
      style={{ background: 'radial-gradient(1200px 700px at 28% -10%, oklch(0.19 0 0), oklch(0.12 0 0))' }}
    >
      <main
        className="scroll-thin absolute flex flex-col gap-3 overflow-y-auto"
        style={{
          top: 90,
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(1180px, calc(100vw - 28px))',
        }}
        aria-label="Data"
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {TABS.map((t) => (
            <PillButton key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
              {t.label}
            </PillButton>
          ))}
          {tab !== 'register' ? (
            <span className="ml-auto flex flex-wrap items-center gap-1.5">
              {tab === 'analytics'
                ? WINDOWS.map((d) => (
                    <PillButton key={d} active={days === d} onClick={() => setDays(d)}>
                      {d} DAYS
                    </PillButton>
                  ))
                : null}
              <PillButton active={includeSimulated} onClick={() => setIncludeSimulated((v) => !v)}>
                INCLUDE SIMULATED
              </PillButton>
            </span>
          ) : null}
        </div>
        {tab === 'register' ? (
          <RegisterPanel />
        ) : tab === 'analytics' ? (
          <Analytics days={days} includeSimulated={includeSimulated} />
        ) : (
          <Performance includeSimulated={includeSimulated} />
        )}
        <span className="label-mono pb-2 text-center text-[8px]" style={{ color: MUTED }}>
          FLIGHT LOGS AND THE REGISTER ARE KEPT FOREVER
        </span>
      </main>
    </div>
  );
}
